import React, { useState, useEffect } from 'react';
import { Box, Grid } from '@mui/material';
import { Button as MUXButton, useKeyPress } from '@manulife/mux';

import CustomButton from '../../../../Components/UI/Buttons/Button';
import { TextLink } from 'application/Components/UI/Input/TextLink';
import { Button } from 'application/Components/UI/Input/Button';
import { selectAppSettings } from 'store/reducers/appSettings/appSettings.slice';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Separator } from 'application/Components/UI/Separator';
import useAdobeDataLayer from 'hooks/useAdobeDataLayer';
import { selectSession } from 'store/reducers/session/session.slice';
import {
  onSubmitNonPotAction,
  onSubmitPassword,
  onSubmitPotAction,
  onSubmitSprntAction,
} from 'store/reducers/login/login.action';
import {
  loginSelector,
  onChange,
  setError,
  setLoading,
  goToStepUsername,
} from 'store/reducers/login/login.slice';
import store from 'store';
import useAppids from 'hooks/useAppids';
import Input from 'application/Components/UI/Input';
import { clickSigninAnotherWayAction } from 'store/reducers/signinAnotherWay/signinAnotherWay.action';
import { signinAnotherWaySelector } from 'store/reducers/signinAnotherWay/signinAnotherWay.slice';
import { YOUR_PASSWORD, BACK_TO_USERNAME } from 'application/enum/login.enum';
import { SQN_SSN_SIGNIN_HEADING } from 'application/enum';

const EnterPassword = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const adobeDataLayer = useAdobeDataLayer();
  const { isRpsAppid, isSprntAppid } = useAppids();

  const { fpwdURL } = useSelector(selectAppSettings);
  const {
    migrationDetails,
    username: usernameRdx,
    password: passwordRdx,
    anotherway,
  } = useSelector(loginSelector);
  const enteredUsername = usernameRdx.value;
  const password = passwordRdx.value;
  const { username: sesionUsername } = useSelector(selectSession);
  const { isAnotherWayLoading, verifyAnotherWayState } = useSelector(
    signinAnotherWaySelector,
  );
  const username = enteredUsername || sesionUsername;

  const [disableButton, setDisableButton] = useState(false);

  // Submit when user presses enter key
  const enterPress = useKeyPress('Enter');
  useEffect(() => {
    if (enterPress) clickSubmit();
  }, [enterPress]);

  // Updates password value when user types
  const handlePasswordChange = (newValue) => {
    dispatch(onChange({ password: newValue }));
    dispatch(setError({ password: false }));
    setDisableButton(false);
  };

  // Handles form submit
  // need to show password field immediately. Username api will continue behind the scenes.
  const clickSubmit = async () => {
    if (passwordRdx.loading || anotherway.loading) return;

    let tempPassword = password?.trim();

    if (!tempPassword) {
      dispatch(onChange({ password: '' }));
      dispatch(setError({ password: 'Password required' }));
      setDisableButton(true);
      return;
    }

    dispatch(setLoading({ password: true }));

    const apiStatus = migrationDetails.apiStatus;
    if (apiStatus === 'pending') {
      await waitForUsernameApi();
    }
    const recentMigrationData = getMigrationDetails();

    if (isRpsAppid) {
      rpsSubmit(recentMigrationData);
    } else if (isSprntAppid) {
      sprntSubmit();
    } else {
      const payload = { adobeDataLayer, navigate };
      dispatch(onSubmitPassword(payload));
    }
  };

  const rpsSubmit = (recentMigrationData) => {
    if (recentMigrationData.isNonPOTFlow) {
      // if user is non-POT -> get jwt from /jwtWsjauth endpoint and finalise() -> form post to /WSJauth
      dispatch(onSubmitNonPotAction({ adobeDataLayer }));
      return;
    }

    const payload = { adobeDataLayer, navigate };
    dispatch(onSubmitPotAction(payload));
  };

  const sprntSubmit = () => {
    const payload = { adobeDataLayer, navigate };
    console.log('sprntSubmit.......payload');
    dispatch(onSubmitSprntAction(payload));
  };

  const waitForUsernameApi = () =>
    new Promise((resolve) => {
      const interval = setInterval(() => {
        const recentMigrationData = getMigrationDetails();
        const apiStatus = recentMigrationData.apiStatus;
        if (apiStatus === 'fulfilled') {
          clearInterval(interval);
          resolve();
        }
      }, 500);
    });

  const getMigrationDetails = () => {
    return store.getState().login?.migrationDetails || {};
  };

  // Handles clicking on sign in another way button
  const clickAnotherWay = () => {
    // Call /getauthtype API
    dispatch(clickSigninAnotherWayAction());
    dispatch(setLoading({ anotherway: true }));
  };

  const onClickforgotPassword = (e) => {
    adobeDataLayer.onClickLink('Forgot Password', fpwdURL);
  };

  const onClickUsername = () => {
    dispatch(goToStepUsername());
  };

  const hideForgotPassword = isSprntAppid;

  return (
    <Grid sx={styles.container}>
      <Box sx={styles.commonBox}>
        <Input label="Username" value={username} disabled={true} />
        <CustomButton type="left-chevron" onClick={onClickUsername}>
          {BACK_TO_USERNAME}
        </CustomButton>
      </Box>

      <Box sx={styles.commonBox}>
        <Input
          type="password"
          label="Password"
          placeholder={YOUR_PASSWORD}
          value={password}
          error={passwordRdx.error}
          onChange={handlePasswordChange}
        />
        {hideForgotPassword ? null : (
          <TextLink
            linkText="Forgot password"
            href={fpwdURL}
            onClick={onClickforgotPassword}
          />
        )}
      </Box>

      <Box sx={styles.btnSection}>
        <Button
          sx={styles.button}
          onClick={clickSubmit}
          loading={passwordRdx.loading || isAnotherWayLoading}
          customErrorMessage="Please add password before continuing"
          disabled={disableButton}
        >
          Sign in
        </Button>
        {!isSprntAppid && verifyAnotherWayState?.enabled ? (
          <>
            <Separator text="or" />
            <Button
              sx={styles.button}
              variant={MUXButton.VARIANT.SECONDARY}
              onClick={clickAnotherWay}
              loading={anotherway.loading}
            >
              {SQN_SSN_SIGNIN_HEADING}
            </Button>
          </>
        ) : null}
      </Box>
    </Grid>
  );
};

const styles = {
  container: {
    width: {
      xs: '100%',
      sm: '400px',
    },
    display: 'flex',
    flexDirection: 'column',
    // alignItems: 'baseline',
    gap: '40px',
    paddingBottom: '50px',
  },
  commonBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  button: {
    width: '100%',
  },
  btnSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    paddingTop: '16px',
  },
};

export default EnterPassword;
